<script setup lang="ts">
  import { nextTick, onUnmounted, ref, watch } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { onClickOutside } from '@vueuse/core'

  import { Bell01Icon, DoubleCheckIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPBadge from '@ui/components/CPBadge.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'
  import CPToggle from '@ui/components/CPToggle.vue'

  import { useNotificationDrawer } from '../composables/useNotificationDrawer'
  import NotificationList from './NotificationList.vue'
  import { notificationWebProps } from './notificationWeb.shared'

  const props = defineProps(notificationWebProps)

  const emit = defineEmits<{
    (e: 'update:notificationListIsOpen'): void
  }>()

  const {
    closeNotificationList,
    currentNotifications,
    handleMarkAllRead,
    handleNotificationItemClick,
    isCurrentQueryFetchingNextPage,
    isCurrentQueryLoading,
    isMarkAllReadDisabled,
    loadMoreTrigger,
    notificationCounterOffset,
    notificationVisibility,
    setNotificationVisibility,
    toggleNotificationList,
    unReadNotificationCount,
  } = useNotificationDrawer(props, emit)

  const { t } = useI18n()

  const ignoreElNotificationIconRef = ref()
  const notificationDropdown = ref<HTMLDivElement>()
  const dropdownPosition = ref({ top: 0, left: 0, right: 0 })
  const dropdownPositionTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const dropdownRetryTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  const handleResize = () => {
    updateDropdownPosition()
  }

  onUnmounted(() => {
    if (process.client) {
      window.removeEventListener('resize', handleResize)
    }

    if (dropdownPositionTimeout.value) {
      clearTimeout(dropdownPositionTimeout.value)
      dropdownPositionTimeout.value = null
    }

    if (dropdownRetryTimeout.value) {
      clearTimeout(dropdownRetryTimeout.value)
      dropdownRetryTimeout.value = null
    }
  })

  onClickOutside(
    notificationDropdown,
    function () {
      emit('update:notificationListIsOpen')
    },
    {
      ignore: [ignoreElNotificationIconRef],
    }
  )

  watch(
    () => props.notificationListIsOpen,
    isOpen => {
      if (isOpen) {
        nextTick(() => {
          updateDropdownPosition()
        })
      }
    }
  )

  watch(
    () => ignoreElNotificationIconRef.value,
    newRef => {
      if (newRef && props.notificationListIsOpen) {
        nextTick(() => {
          updateDropdownPosition()
        })
      }
    }
  )

  if (process.client) {
    window.addEventListener('resize', handleResize)
  }

  function updateDropdownPosition() {
    if (!ignoreElNotificationIconRef.value) {
      return
    }

    const bellIconRect = ignoreElNotificationIconRef.value.getBoundingClientRect()

    if (bellIconRect.width === 0 || bellIconRect.height === 0) {
      console.warn('Bell icon has zero dimensions, retrying position calculation')
      return
    }

    const viewportWidth = window.innerWidth
    const dropdownWidth = Math.min(432, Math.max(viewportWidth - 16, 320))
    const gap = 12

    let leftPosition = 0

    if (props.position === 'end') {
      leftPosition = bellIconRect.right - dropdownWidth
    } else {
      leftPosition = bellIconRect.left
    }

    leftPosition = Math.max(8, Math.min(leftPosition, viewportWidth - dropdownWidth - 8))

    const newPosition = {
      top: bellIconRect.bottom + gap,
      left: leftPosition,
      right: 0,
    }

    if (JSON.stringify(dropdownPosition.value) !== JSON.stringify(newPosition)) {
      dropdownPosition.value = newPosition
    }
  }

  function handleToggleNotificationList() {
    if (props.notificationListIsOpen) {
      closeNotificationList()
      return
    }

    updateDropdownPosition()

    nextTick(() => {
      updateDropdownPosition()

      if (dropdownPositionTimeout.value) {
        clearTimeout(dropdownPositionTimeout.value)
      }

      if (dropdownRetryTimeout.value) {
        clearTimeout(dropdownRetryTimeout.value)
        dropdownRetryTimeout.value = null
      }

      dropdownPositionTimeout.value = setTimeout(() => {
        updateDropdownPosition()

        if (dropdownPosition.value.top === 0 && dropdownPosition.value.left === 0) {
          console.warn('Dropdown position still at origin, forcing retry')
          dropdownRetryTimeout.value = setTimeout(() => {
            updateDropdownPosition()
          }, 50)
        }
      }, 10)
    })

    toggleNotificationList()
  }
</script>

<template>
  <div class="z-20 font-semibold text-clientportal-sidebar-font-secondary">
    <div
      :id="createElementId('navbar', 'notification', 'bell-icon')"
      ref="ignoreElNotificationIconRef"
      role="button"
      tabindex="0"
      class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg transition-colors duration-150 hover:bg-hover"
      :class="[!profileCardIsOpen && !appLauncherIsOpen && notificationListIsOpen ? 'bg-hover' : '']"
      @click="handleToggleNotificationList"
      @keydown.enter.prevent="handleToggleNotificationList"
      @keydown.space.prevent="handleToggleNotificationList"
    >
      <CPBadge
        :id="createElementId('navbar', 'notification', 'badge')"
        class="notification-bell-badge"
        :offset="notificationCounterOffset"
        :show="unReadNotificationCount > 0"
        :value="unReadNotificationCount > 9 ? '9+' : unReadNotificationCount"
      >
        <Bell01Icon class="h-5 w-5 text-on-sidebar" />
      </CPBadge>
    </div>

    <Transition
      enter-from-class="opacity-0 translate-y-1"
      enter-active-class="transition ease-out duration-200"
      enter-to-class="opacity-100 translate-y-0"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
      leave-active-class="transition ease-in duration-150"
    >
      <div
        v-if="!profileCardIsOpen && !appLauncherIsOpen && notificationListIsOpen"
        :id="createElementId('navbar', 'notification', 'dropdown')"
        ref="notificationDropdown"
        class="notification-dropdown fixed z-10 flex max-h-[80vh] w-[calc(100vw-1rem)] max-w-[27rem] flex-col overflow-hidden rounded-xl border border-solid border-default bg-surface shadow-[0_24px_64px_rgba(15,23,42,0.18)] box-border md:max-h-[44rem]"
        :style="{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
        }"
      >
        <div
          :id="createElementId('navbar', 'notification', 'dropdown-header')"
          class="flex items-start justify-between gap-3 border-b border-default px-5 pb-4 pt-5"
        >
          <div class="min-w-0">
            <CPText :id="createElementId('navbar', 'notification', 'dropdown-title')" size="2xl" weight="semibold" class="text-body">
              {{ t('notifications.title') }}
            </CPText>
          </div>
          <div :id="createElementId('navbar', 'notification', 'header-actions')" class="flex items-center gap-1">
            <CPButton
              :id="createElementId('navbar', 'notification', 'mark-all-read-btn')"
              type="text"
              size="sm"
              :disabled="isMarkAllReadDisabled"
              :class="['h-auto px-0 py-0', isMarkAllReadDisabled ? 'opacity-50 cursor-not-allowed' : '']"
              @click="handleMarkAllRead"
            >
              <DoubleCheckIcon class="mr-1.5 h-4 w-4 text-600" />
              <CPText size="sm" weight="semibold" class="text-700">
                {{ t('notifications.markAllRead') }}
              </CPText>
            </CPButton>
          </div>
        </div>
        <div class="notification-filter-section border-b border-default px-5 py-3.5">
          <div :id="createElementId('navbar', 'notification', 'unread-filter-row')" class="flex items-center gap-2">
            <CPToggle
              :id="createElementId('navbar', 'notification', 'toggle-btn')"
              size="sm"
              :value="notificationVisibility === 'unread'"
              color="blue"
              @update:value="(value: boolean) => setNotificationVisibility(value ? 'unread' : 'all')"
            />
            <CPText :id="createElementId('navbar', 'notification', 'show-unread-label')" size="sm" class="text-body">
              {{ t('notifications.showOnlyUnread') }}
            </CPText>
          </div>
        </div>
        <div class="notification-drawer-body flex-1 overflow-y-auto px-2 pb-2 pt-1">
          <NotificationList
            :notifications="currentNotifications"
            :is-loading="isCurrentQueryLoading"
            :is-fetching-next-page="isCurrentQueryFetchingNextPage"
            @itemClicked="handleNotificationItemClick"
          />
          <div
            :id="createElementId('navbar', 'notification', 'load-more-trigger')"
            ref="loadMoreTrigger"
            class="h-4"
            aria-hidden="true"
          ></div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
  .notification-bell-badge :deep(.hr-badge-sup) {
    background-color: var(--cp-error-500);
  }

  .notification-bell-badge :deep(.hr-badge-sup .hr-base-slot-machine) {
    color: var(--cp-text-on-error) !important;
  }

  .notification-dropdown {
    background-color: var(--cp-background-500);
    backdrop-filter: blur(10px);
    box-shadow: 0 24px 64px var(--cp-shadow-strong);
  }

  .notification-drawer-body {
    background-color: var(--cp-background-500);
  }

  .notification-filter-section {
    background-color: var(--cp-background-500);
  }
</style>
